(() => {
  const storeConfigCache = new Map();
  const bundleConfigCache = new Map();

  const rootPath = () => window.Shopify?.routes?.root || '/';
  const shopDomain = () => window.Shopify?.shop;

  const getJson = async (url, options = {}) => {
    const response = await fetch(url, options);
    const json = await response.json();
    if (!response.ok) {
      throw new Error(json?.description || json?.message || 'Loop bundle information could not be loaded.');
    }
    return json;
  };

  const getStoreConfig = async () => {
    const domain = shopDomain();
    if (!domain) throw new Error('The Shopify store domain is unavailable.');
    if (!storeConfigCache.has(domain)) {
      storeConfigCache.set(domain, getJson(`https://cdn.loopwork.co/${domain}/store.json`));
    }
    return storeConfigCache.get(domain);
  };

  const getBundleConfig = async (productId) => {
    const key = String(productId);
    if (!bundleConfigCache.has(key)) {
      bundleConfigCache.set(key, (async () => {
        const storeConfig = await getStoreConfig();
        const presetIds = (storeConfig.presetBundleShopifyProductIds || []).map(String);
        if (!presetIds.includes(key)) throw new Error('This set is not active in Loop.');
        const bundleConfig = await getJson(
          `https://cdn.loopwork.co/${shopDomain()}/presetBundles/${key}.json`
        );
        return { storeConfig, bundleConfig };
      })());
    }
    return bundleConfigCache.get(key);
  };

  const findBundleVariant = (bundleConfig, variantId) =>
    (bundleConfig.variants || []).find((variant) => String(variant.shopifyId) === String(variantId));

  const findDiscount = (bundleVariant, sellingPlanId) =>
    (bundleVariant.mappedDiscounts || []).find((discount) =>
      sellingPlanId ? discount.purchaseType === 'SUBSCRIPTION' : discount.purchaseType !== 'SUBSCRIPTION'
    );

  const syncBundleStructuredData = (bundleConfig) => {
    const node = document.querySelector('[data-tal-bundle-schema]');
    if (!node) return;
    try {
      const product = JSON.parse(node.textContent);
      const offers = Array.isArray(product.offers) ? product.offers : [product.offers];
      for (const offer of offers) {
        if (!offer?.url) continue;
        const variantId = new URL(offer.url, window.location.origin).searchParams.get('variant');
        const variant = findBundleVariant(bundleConfig, variantId);
        if (!variant) continue;
        const price = Number(variant.oneTimePrice);
        if (variant.oneTimePrice == null || !Number.isFinite(price) || price <= 0) continue;
        offer.price = Math.round(price * 100) / 100;
        offer.availability = variant.outOfStock || !findDiscount(variant, null)
          ? 'https://schema.org/OutOfStock'
          : 'https://schema.org/InStock';
      }
      node.textContent = JSON.stringify(product);
    } catch (error) {
      node.setAttribute('data-schema-sync-error', error.message);
    }
  };

  const formatPercent = (value) => {
    const rounded = Math.round(value * 10) / 10;
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  };

  // Loop's aggregate Form + Function subscription price lands on a half-cent,
  // while Shopify rounds the two discounted child lines to $76.85. Match the
  // PDP to the exact cart/checkout total instead of showing $76.86.
  const subscriptionDisplayAdjustment = (widget) =>
    widget.dataset.productHandle === 'the-strong-girl-set' ? -1 : 0;

  window.alsGetPresetBundlePrices = async (preset) => {
    const { bundleConfig } = await getBundleConfig(preset.id);
    const variant = findBundleVariant(bundleConfig, preset.variantId);
    if (!variant || variant.outOfStock) return null;
    const subscription = variant.sellingPlanPrices?.[String(preset.planId)];
    return {
      oneTime: findDiscount(variant, null) ? Math.round(Number(variant.oneTimePrice) * 100) : null,
      subscription: subscription == null || !findDiscount(variant, preset.planId) ? null :
        Math.round(Number(subscription) * 100) + subscriptionDisplayAdjustment({ dataset: { productHandle: preset.handle } })
    };
  };

  const syncWidgetPricing = async (widget) => {
    const productId = widget.dataset.productId;
    const pricingNode = widget.querySelector('[data-subscription-pricing]');
    const variantInput = widget.closest('form')?.querySelector('[name="id"]');
    if (!productId || !pricingNode || !variantInput) return;

    try {
      const { bundleConfig } = await getBundleConfig(productId);
      syncBundleStructuredData(bundleConfig);
      const bundleVariant = findBundleVariant(bundleConfig, variantInput.value);
      if (!bundleVariant) throw new Error('The selected set option is unavailable.');

      const pricing = JSON.parse(pricingNode.textContent);
      const record = pricing[String(variantInput.value)];
      if (!record) return;

      const planId = String(record.planId || '');
      record.oneTime = Math.round(Number(bundleVariant.oneTimePrice) * 100);
      if (bundleVariant.sellingPlanPrices?.[planId] != null) {
        record.subscription = Math.round(Number(bundleVariant.sellingPlanPrices[planId]) * 100) +
          subscriptionDisplayAdjustment(widget);
      }
      pricingNode.textContent = JSON.stringify(pricing);

      const badge = widget.querySelector('.als-purchase-option--subscription .als-purchase-option-badge');
      if (badge && record.base > 0) {
        const savings = (1 - record.subscription / record.base) * 100;
        badge.textContent = `Save ${formatPercent(savings)}% total`;
      }

      widget.removeAttribute('data-loop-loading');
      widget.dispatchEvent(new Event('change', { bubbles: true }));
    } catch (error) {
      widget.setAttribute('data-loop-error', error.message);
    }
  };

  const readExistingDiscountAttributes = async () => {
    try {
      const cart = await getJson(`${rootPath()}cart.js`, { headers: { Accept: 'application/json' } });
      const existing = cart.attributes?._loopBundleDiscountAttributes
        ? JSON.parse(cart.attributes._loopBundleDiscountAttributes)
        : {};
      const activeIds = new Set(
        cart.items
          .map((item) => item.properties?._bundleId || item.properties?._loopBundleTxnId)
          .filter(Boolean)
      );
      return Object.keys(existing)
        .filter((key) => activeIds.has(key))
        .reduce((result, key) => {
          result[key] = existing[key];
          return result;
        }, {});
    } catch (_error) {
      return {};
    }
  };

  const createBundleTransaction = async ({ storeConfig, productId, variantId, quantity, sellingPlanId, discount }) => {
    const endpoint = storeConfig.apiUrl?.bundleTransaction ||
      'https://sentinel.loopwork.co/bundles/createPresetBundleTransaction';
    const payload = {
      presetProductShopifyId: Number(productId),
      presetDiscountId: discount.id,
      presetVariantShopifyId: Number(variantId),
      totalQuantity: Number(quantity),
      sellingPlanShopifyId: sellingPlanId ? Number(sellingPlanId) : 0
    };
    const response = await getJson(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        authorization: storeConfig.sentinalAuthToken
      },
      body: JSON.stringify(payload)
    });
    const transactionId = response?.data?.transactionId;
    if (!transactionId) throw new Error('Loop could not create this set.');
    return transactionId;
  };

  const addPresetBundle = async (form, widget) => {
    const formData = new FormData(form);
    const productId = widget.dataset.productId;
    const variantId = formData.get('id');
    const quantity = Number(formData.get('quantity') || 1);
    const sellingPlanId = formData.get('selling_plan') || null;
    const { storeConfig, bundleConfig } = await getBundleConfig(productId);
    const bundleVariant = findBundleVariant(bundleConfig, variantId);

    if (!bundleVariant || bundleVariant.outOfStock) {
      throw new Error('This set is unavailable right now.');
    }

    const discount = findDiscount(bundleVariant, sellingPlanId);
    if (!discount) throw new Error('This purchase option is unavailable for this set.');

    const transactionId = await createBundleTransaction({
      storeConfig,
      productId,
      variantId,
      quantity,
      sellingPlanId,
      discount
    });

    const existingAttributes = await readExistingDiscountAttributes();
    const computedDiscount = sellingPlanId
      ? Number(discount.sellingPlanComputedDiscounts?.[String(sellingPlanId)] || 0)
      : Number(discount.oneTimeDiscount || 0);

    const attributes = {
      _loopBundleDiscountAttributes: JSON.stringify({
        ...existingAttributes,
        [transactionId]: {
          discountType: discount.type,
          discountValue: discount.value,
          discountComputedValue: computedDiscount * quantity
        }
      })
    };

    const items = (bundleVariant.mappedProductVariants || []).map((child) => {
      const item = {
        id: Number(child.shopifyId),
        quantity: Number(child.quantity) * quantity,
        properties: {
          _bundleId: transactionId,
          _isPresetBundleProduct: true,
          _bundleName: bundleConfig.name,
          _bundleProductUrl: window.location.pathname
        }
      };
      if (sellingPlanId) item.selling_plan = Number(sellingPlanId);
      return item;
    });

    if (!items.length) throw new Error('No products are connected to this set.');

    const result = await getJson(`${rootPath()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, attributes })
    });
    return result;
  };

  const showError = (widget, message) => {
    widget.querySelector('[data-loop-form-error]')?.remove();
    const error = document.createElement('p');
    error.className = 'als-purchase-option-error';
    error.setAttribute('data-loop-form-error', '');
    error.setAttribute('role', 'alert');
    error.textContent = message || 'This set could not be added. Please try again.';
    widget.appendChild(error);
  };

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[action*="/cart/add"]');
    const widget = form?.querySelector('[data-subscription-widget][data-is-bundle="true"]');
    if (!form || !widget) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const submit = form.querySelector('[data-add-to-cart]');
    const originalText = submit?.textContent;
    if (submit) {
      submit.disabled = true;
      submit.setAttribute('aria-busy', 'true');
      submit.textContent = 'Adding your set…';
    }

    try {
      await addPresetBundle(form, widget);
      if (typeof window.alsOpenCartDrawer === 'function') {
        await window.alsOpenCartDrawer(submit || form);
      } else {
        window.location.assign(`${rootPath()}cart`);
      }
    } catch (error) {
      showError(widget, error.message);
    } finally {
      if (submit) {
        submit.disabled = false;
        submit.removeAttribute('aria-busy');
        submit.textContent = originalText;
      }
    }
  }, true);

  const syncCardPricing = async (card) => {
    try {
      const { bundleConfig } = await getBundleConfig(card.dataset.productId);
      const variant = findBundleVariant(bundleConfig, card.dataset.variantId);
      const amount = variant?.sellingPlanPrices?.[card.dataset.planId];
      if (amount == null || !Number.isFinite(Number(amount))) return;
      const cents = Math.round(Number(amount) * 100) + subscriptionDisplayAdjustment(card);
      const price = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency', currency: card.dataset.currency
      }).format(cents / 100);
      const link = card.querySelector('a.tv-card-cta');
      if (link) link.textContent = card.dataset.priceLabel.replace('__PRICE__', price);
    } catch (error) {
      card.setAttribute('data-loop-error', error.message);
    }
  };

  const initialize = () => {
    document.querySelectorAll('[data-bundle-card-subscription]').forEach(syncCardPricing);
    document
      .querySelectorAll('[data-subscription-widget][data-is-bundle="true"]')
      .forEach((widget) => {
        widget.setAttribute('data-loop-loading', 'true');
        syncWidgetPricing(widget);
      });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
